import React, { useState, useEffect, useCallback } from 'react';

// Logo used for the watermark overlay (served from /public)
const WATERMARK_URL = '/Ratified-Logo-Main.svg';

// Diagonal watermark grid positions (in % of the PDF pane) — 3×3 lattice,
// matching the agency certificates register screen
const WATERMARK_GRID = [
  { top: 15, left: 20 },
  { top: 15, left: 50 },
  { top: 15, left: 80 },
  { top: 50, left: 20 },
  { top: 50, left: 50 },
  { top: 50, left: 80 },
  { top: 85, left: 20 },
  { top: 85, left: 50 },
  { top: 85, left: 80 },
];

/**
 * Read-only certificate PDF viewer.
 *
 * - Fetches the PDF as an authenticated blob from `certificate.pdf_download_url`
 *   so the raw (shareable) URL never appears in the DOM / address bar.
 * - `#toolbar=0&navpanes=0` hides download/print buttons in Chromium PDF viewers.
 * - Right-click is disabled over the modal.
 * - A diagonal logo watermark sits over the PDF (pointer-events: none).
 *
 * Props:
 *  - certificate: the certificate object to display (null/undefined renders nothing)
 *  - customerName: display name of the customer the certificate belongs to
 *  - onClose: called when the viewer should close (X, backdrop click, ESC)
 */
const CertificatePdfViewer = ({ certificate, customerName, onClose }) => {
  const [pdf, setPdf] = useState({ url: null, loading: false, error: null });

  const handleCloseViewer = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // Load the PDF as a blob whenever a certificate is passed in
  useEffect(() => {
    if (!certificate?.pdf_download_url) return undefined;

    let cancelled = false;
    let objectUrl = null;

    const loadPdf = async () => {
      setPdf({ url: null, loading: true, error: null });

      try {
        const BASE_URL = process.env.REACT_APP_BASE_URL;
        let downloadUrl = certificate.pdf_download_url;

        // Allow the API to return either a full URL or a relative path
        if (!/^https?:\/\//i.test(downloadUrl)) {
          downloadUrl = `${BASE_URL}${downloadUrl}`;
        }

        const token = localStorage.getItem('jwtToken');
        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(downloadUrl, { headers });

        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          window.location.href = '/login';
          return;
        }

        if (!response.ok) {
          throw new Error(
            'Could not load the certificate PDF. Please try again.',
          );
        }

        const blob = await response.blob();

        // Some API errors come back as JSON with a 200 — catch those too
        if (blob.type && blob.type.includes('json')) {
          let message = 'Could not load the certificate PDF.';
          try {
            const parsed = await blob.json();
            if (parsed?.message) message = parsed.message;
          } catch (e) {
            /* ignore parse errors */
          }
          throw new Error(message);
        }

        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setPdf({ url: objectUrl, loading: false, error: null });
        }
      } catch (err) {
        if (!cancelled) {
          setPdf({
            url: null,
            loading: false,
            error: err.message || 'Network error, please try again later',
          });
        }
      }
    };

    loadPdf();

    // Revoke the blob URL when the viewer closes / certificate changes
    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };
  }, [certificate]);

  // ESC closes the viewer
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleCloseViewer();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleCloseViewer]);

  if (!certificate) return null;

  const displayName = customerName || certificate.customer_name || 'Customer';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-100/80 p-4 backdrop-blur-sm"
      onClick={handleCloseViewer}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        className="flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Viewer header */}
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <img
              src={WATERMARK_URL}
              alt=""
              aria-hidden="true"
              className="h-8 w-8 shrink-0 rounded-lg bg-slate-50 object-contain p-1 ring-1 ring-slate-100"
            />
            <div className="min-w-0 leading-tight">
              <h3 className="truncate text-[13.5px] font-bold text-slate-800">
                {certificate.certificate_number}
              </h3>
              <p className="truncate text-[11px] font-medium text-slate-400">
                {displayName} · Read Only
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCloseViewer}
            className="shrink-0 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* PDF embed + LOGO watermark overlay */}
        <div className="relative min-h-0 flex-1 bg-slate-100">
          {pdf.url && (
            <iframe
              title={`certificate-${certificate.certificate_number}`}
              src={`${pdf.url}#toolbar=0&navpanes=0&view=FitH`}
              className="h-full w-full border-0"
            />
          )}
          {!pdf.url && (
            <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
              {pdf.loading ? (
                <span className="inline-flex items-center gap-2 text-[13px] font-medium text-slate-400">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-[#375AE7]" />
                  Loading certificate PDF…
                </span>
              ) : (
                <span className="text-[13px] font-medium text-red-600">
                  {pdf.error}
                </span>
              )}
            </div>
          )}
          {/* Diagonal logo watermark — sits over the PDF */}
          <div className="pointer-events-none absolute inset-0 select-none overflow-hidden">
            {WATERMARK_GRID.map(({ top, left }) => (
              <img
                key={`${top}-${left}`}
                src={WATERMARK_URL}
                alt=""
                aria-hidden="true"
                className="absolute h-24 w-auto opacity-[0.10]"
                style={{
                  top: `${top}%`,
                  left: `${left}%`,
                  transform: 'translate(-50%, -50%) rotate(-28deg)',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificatePdfViewer;
